// ABOUTME: Button group component for modal footers and action bars
// ABOUTME: Renders save/cancel/delete buttons with consistent styling

import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Trash2Icon } from "../icons"
import useDevice from "@shared/hooks/useDevice"
import { CButton, type FlowbiteColor } from "./CButton"
import { cn } from "@shared/lib/utils"

export interface IBtnProps {
  freeContent?: boolean
  /** Draw the icon alone; the label becomes the button's title and aria-label. */
  iconOnly?: boolean
  type?: "save" | "recalc" | "cancel" | "delete" | "custom"
  position?: "left" | "right"
  action?: (e?: React.MouseEvent<HTMLElement>) => void
  contentText?: string | ReactNode
  icon?: ReactNode
  disabled?: boolean
  loading?: boolean
  className?: string
}

interface _ButtonInnerProps {
  btn: IBtnProps
}

interface IProps {
  btns: IBtnProps[]
}

export const CButtonGroup = (props: IProps) => {
  const { btns } = props
  const { t } = useTranslation()
  const { isPWA } = useDevice()

  const PWAPaddingBottom = isPWA ? "pb-7" : ""

  const getButton = (showIn: "right" | "left", b: IBtnProps, index: number) => {
    const { btnColor, btnClassName, position, type, btnName, btnImage } = getButtonStyle(b)
    if (position !== showIn) return
    if (b.freeContent) return <div key={index}>{b.contentText}</div>

    if (b.type === "custom") {
      return (
        <CButton
          type="button"
          size="md"
          key={index}
          onClick={b.action}
          className={cn("max-md:min-h-11", btnClassName, b.className)}
          disabled={b.disabled || b.loading}
          label={btnName}
          icon={btnImage}
          isProcessing={b.loading}
        />
      )
    }

    if (b.iconOnly) {
      const label = typeof btnName === "string" ? btnName : undefined
      return (
        <CButton
          type={type === "submit" ? "submit" : "button"}
          size="md"
          key={index}
          color={btnColor}
          onClick={b.action}
          className={cn("max-md:min-h-11 flex-none !px-3", b.className)}
          disabled={b.disabled || b.loading}
          icon={btnImage}
          title={label}
          aria-label={label}
          isProcessing={b.loading}
        />
      )
    }

    return (
      <CButton
        type={type === "submit" ? "submit" : "button"}
        size="md"
        key={index}
        color={btnColor}
        onClick={b.action}
        className={cn("max-md:min-h-11", btnClassName)}
        disabled={b.disabled || b.loading}
        label={btnName}
        icon={btnImage}
        isProcessing={b.loading}
      />
    )
  }

  const getButtonStyle = (
    btn: IBtnProps,
  ): {
    type?: string
    btnColor: FlowbiteColor
    btnName: string | ReactNode
    btnImage?: ReactNode
    btnClassName: string
    position: "left" | "right"
  } => {
    // min-w-0 so a nowrap label can't push its grid column wider than half the
    // row; tighter padding and a wrapping label so the text still fits.
    const btnClassName = "max-md:flex-1 max-md:min-w-0 max-md:px-3 max-md:whitespace-normal"
    switch (btn.type) {
      case "delete":
        return {
          btnColor: "gray",
          btnName: btn.contentText || t("common.delete"),
          btnImage: btn.icon || <Trash2Icon />,
          btnClassName: "",
          position: btn.position || "right",
        }
      case "cancel":
        return {
          btnColor: "gray",
          btnName: btn.contentText || t("common.cancel"),
          btnImage: btn.icon,
          btnClassName,
          position: btn.position || "left",
        }
      case "recalc":
        return {
          btnColor: "orange",
          btnName: btn.contentText || t("common.recalc"),
          btnImage: btn.icon,
          btnClassName,
          position: btn.position || "left",
        }
      case "save":
        return {
          type: "submit",
          btnColor: "blue",
          btnName: btn.contentText || t("common.save"),
          btnImage: btn.icon,
          btnClassName,
          position: btn.position || "left",
        }
      case "custom":
        return {
          btnColor: "gray",
          btnName: btn.contentText,
          btnImage: btn.icon,
          btnClassName,
          position: btn.position || "left",
        }
      case undefined:
      default:
        return {
          btnColor: "gray",
          btnName: btn.contentText || "",
          btnImage: btn.icon,
          btnClassName,
          position: btn.position || "left",
        }
    }
  }

  return (
    // The modal footer of the design system: 14/20/18 above a hairline, 8px
    // between buttons (docs/DESIGN_SYSTEM.md §4.7). cn() so the PWA's extra
    // bottom padding wins over the default instead of racing it in the sheet.
    <div
      className={cn(
        "bottom-0 right-0 flex w-full items-center justify-between gap-2 border-t border-border bg-white px-5 pt-3.5 pb-4.5 max-md:px-4",
        PWAPaddingBottom,
      )}
    >
      {/* One row at every width. The buttons may narrow (min-w-0, tighter
          padding, wrapping labels) rather than push the footer past the screen —
          that is what used to force them onto a second line. */}
      <div className="flex min-w-0 items-start gap-2 max-md:flex-1">
        {btns.map((b, index) => getButton("left", b, index))}
      </div>
      <div className="flex items-start gap-2 shrink-0">
        {btns.map((b: IBtnProps, index) => getButton("right", b, index))}
      </div>
    </div>
  )
}
